import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { getPatientByExternalId } from "../../../api/internal";
import { EhrSources, PatientBasicInfo } from "../../../api/schemas/patient";
import useMetriportToast from "../../shared/toast";
import { Tag } from "../../shared/Tag";

type PatientLookupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  cxId: string;
};

const sourceOptions = Object.entries(EhrSources).map(([key, value]) => ({
  key,
  value,
}));

export default function PatientLookupModal({ isOpen, onClose, cxId }: PatientLookupModalProps) {
  const toast = useMetriportToast();
  const [externalId, setExternalId] = useState("");
  const [source, setSource] = useState<EhrSources | "">("");
  const [isLoading, setIsLoading] = useState(false);
  const [patient, setPatient] = useState<PatientBasicInfo | undefined>(undefined);
  const [searched, setSearched] = useState(false);

  function handleClose() {
    setExternalId("");
    setSource("");
    setPatient(undefined);
    setSearched(false);
    onClose();
  }

  async function handleSearch() {
    if (!externalId.trim()) {
      toast.warning({ title: "Please enter an external ID" });
      return;
    }
    if (!source) {
      toast.warning({ title: "Please select a source" });
      return;
    }

    setIsLoading(true);
    setSearched(false);
    setPatient(undefined);

    try {
      const result = await getPatientByExternalId({
        cxId,
        externalId: externalId.trim(),
        source,
      });
      setPatient(result);
      setSearched(true);
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMsg =
        axiosError?.response?.data?.detail ?? axiosError?.message ?? "Failed to look up patient";
      toast.error({ title: errorMsg });
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      handleSearch();
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Find Patient by External ID</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel>Source</FormLabel>
            <Select
              placeholder="Select source"
              value={source}
              onChange={e => setSource(e.target.value as EhrSources)}
            >
              {sourceOptions.map(({ key, value }) => (
                <option key={key} value={value}>
                  {key}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>External ID</FormLabel>
            <Flex gap={2}>
              <Input
                value={externalId}
                onChange={e => setExternalId(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter external ID"
              />
              <Button colorScheme="blue" onClick={handleSearch} isDisabled={isLoading}>
                {isLoading ? <Spinner size="sm" /> : "Search"}
              </Button>
            </Flex>
          </FormControl>

          {searched && (
            <Box mt={4} p={4} bg="gray.50" borderRadius="md">
              {patient ? (
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    Patient Found
                  </Text>
                  <Flex mb={2}>
                    <Tag id="patientId" label={patient.id} canCopy />
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    {patient.firstName} {patient.lastName}
                  </Text>
                </Box>
              ) : (
                <Text color="gray.500">No patient found with this external ID</Text>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
